import { ApolloError, useMutation } from "@apollo/client";
import { KIND } from "baseui/button";
import { AuthorType } from "components/author-selector/author-selector";
import { ConfirmDialog } from "components/confirm-dialog";
import { Content } from "components/content";
import { FormRenderer } from "components/form-renderer";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { Category } from "containers/Categories/categories";
import { Tag } from "containers/Tags/tags";
import { User } from "containers/Users/users";
import { useAuth } from "contexts/auth-context";
import { useLoading } from "contexts/loading-context";
import { useProject } from "contexts/project-context";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { DeviceFloppy, FileOff, Note } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";
import { scrollOnError } from "utils/scrollOnError";

import { ARTICLES_FIELDS, ArticlesFormInputs } from "../articles.form";
import { ARTICLES_CREATE } from "../articles.gql";

export default function ArticlesCreate(): React.ReactElement {
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(
    false
  );
  const [isClearConfirmDialogOpen, setIsClearConfirmDialogOpen] = useState(
    false
  );
  const [errorTimeStamp, setErrorTimeStamp] = useState<number>();
  const { isLoading, setIsLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();
  const { checkPermission } = useAuth();
  const history = useHistory();
  const { user } = useAuth();
  const { activeProjectId } = useProject();

  const {
    control,
    formState: { errors, isDirty, ...formState },
    handleSubmit,
    reset,
    setValue,
    watch,
    ...methods
  } = useForm<ArticlesFormInputs>({
    defaultValues: {
      name: "",
      slug: "",
      lead: "",
      isVisible: false,
      tags: [],
      metaTitle: "",
      metaDescription: "",
      metaKeywords: "",
      ogTitle: "",
      ogDescription: "",
      robots: [],
      canonical: "",
      author: user
        ? {
            type: AuthorType.User,
            value: user,
          }
        : undefined,
    },
  });

  const watchProject = watch("project");

  useEffect(() => {
    setValue("language", []);
    setValue("categories", []);
    setValue("tags", []);

    if (user) {
      setValue("author", {
        type: AuthorType.User,
        value: user,
      });
    }
  }, [activeProjectId, watchProject]);

  const [createArticle, { error }] = useMutation(ARTICLES_CREATE);

  const onError = (errors: Record<string, unknown>, e: any) =>
    setErrorTimeStamp(e.timeStamp);

  useEffect(() => {
    if (errors) scrollOnError(errors, ARTICLES_FIELDS, "create");
  }, [errorTimeStamp]);

  const onSubmit = async ({
    project,
    language,
    categories,
    isVisible,
    tags,
    metaTitle,
    metaDescription,
    metaKeywords,
    ogTitle,
    ogDescription,
    canonical,
    robots,
    author,
    ...values
  }: ArticlesFormInputs): Promise<void> => {
    setIsLoading(true);

    try {
      const { data } = await createArticle({
        variables: {
          articleCreateInput: {
            projectId: activeProjectId || project?.[0]?.id,
            language: language?.[0]?.id,
            categoryIds: categories?.map((category: Category) => category?.id),
            isVisible: !!isVisible,
            tagIds: tags?.map((tag: Tag) => tag?.id),
            ...values,
          },
          authorAssignInput: {
            ...(author.type === AuthorType.User && {
              userId: (author.value as User).id,
            }),
            ...(author.type === AuthorType.AuthorName && {
              authorName: author.value,
            }),
          },
          seoTagsetUpsertInput: {
            metaTitle,
            metaDescription,
            metaKeywords,
            ogTitle,
            ogDescription,
            canonical,
            robots: robots?.length ? robots?.[0]?.label : null,
          },
        },
      });

      enqueueSnackbar({
        message: "Utworzono pomyślnie",
        variant: "success",
      });
      history.push(`/articles/${data?.articleCreate?.id}`);
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!checkPermission(PERMISSIONS.article.create))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title="Nowy artykuł"
        labels={["Artykuły", "Tworzenie"]}
        goBackOption
        buttons={[
          {
            label: "Wyczyść",
            kind: KIND.secondary,
            startEnhancer: <Note size={18} />,
            disabled: isLoading,
            onClick: () => {
              setIsClearConfirmDialogOpen(true);
            },
          },
          {
            label: "Anuluj",
            kind: KIND.secondary,
            startEnhancer: <FileOff size={18} />,
            disabled: isLoading,
            onClick: () =>
              isDirty ? setIsCancelConfirmDialogOpen(true) : history.goBack(),
          },
          {
            label: "Utwórz",
            kind: KIND.primary,
            startEnhancer: <DeviceFloppy size={18} />,
            onClick: handleSubmit(onSubmit, onError),
            disabled: isLoading,
            isLoading: isLoading,
            type: "submit",
            formId: "createArticle",
          },
        ]}
      />
      <Content>
        <FormProvider<ArticlesFormInputs>
          control={control}
          formState={{ errors, isDirty, ...formState }}
          handleSubmit={handleSubmit}
          reset={reset}
          watch={watch}
          setValue={setValue}
          {...methods}
        >
          <FormRenderer<ArticlesFormInputs>
            id="createArticle"
            onSubmit={handleSubmit(onSubmit, onError)}
            type="create"
            isLoading={isLoading}
            error={error}
            fields={ARTICLES_FIELDS}
          />
        </FormProvider>
        <ConfirmDialog
          isOpen={isCancelConfirmDialogOpen}
          label="Anulowanie tworzenia nowego artykułu"
          close={() => setIsCancelConfirmDialogOpen(false)}
          confirm={() => history.goBack()}
        />
        <ConfirmDialog
          isOpen={isClearConfirmDialogOpen}
          label="Wyczyszczenie formularza"
          close={() => setIsClearConfirmDialogOpen(false)}
          confirm={() => {
            reset();
            setIsClearConfirmDialogOpen(false);
          }}
        />
      </Content>
    </article>
  );
}
